import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Instagram } from "lucide-react";
import { toast } from "sonner";
import { useLang } from "../i18n/LanguageContext";
import { subscribe } from "../lib/api";

export const Footer = () => {
  const { t, lang } = useLang();
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);

  const onSubscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    setBusy(true);
    try {
      await subscribe(email, lang);
      toast.success(t("common.subscribedMsg"));
      setEmail("");
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  const links = [
    { to: "/philosophy", label: t("nav.philosophy") },
    { to: "/method", label: t("nav.method") },
    { to: "/services", label: t("nav.services") },
    { to: "/founder", label: t("nav.founder") },
    { to: "/journal", label: t("nav.journal") },
    { to: "/apply", label: t("nav.contact") },
  ];

  return (
    <footer data-testid="main-footer" className="relative z-10 border-t border-white/10 bg-kenosis-black">
      <div className="mx-auto max-w-[1400px] px-6 md:px-10 py-20 md:py-28">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-14">
          <div className="md:col-span-5">
            <div className="flex flex-col items-start leading-none">
              <span className="font-serif text-3xl tracking-[0.35em] text-kenosis-ivory pl-[0.35em]">KENOSIS</span>
              <span className="font-sans text-[0.6rem] tracking-[0.55em] text-kenosis-gold pl-[0.55em] mt-1">METHOD</span>
            </div>
            <p className="mt-8 max-w-md font-sans text-sm font-light leading-relaxed text-kenosis-muted">
              {t("common.footerTagline")}
            </p>
            <p className="mt-6 font-sans text-xs tracking-wide text-kenosis-gold/80">
              {t("common.europeBased")}
            </p>
            <a
              href="https://instagram.com/aaron.kenosis"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="footer-instagram"
              aria-label="Follow @aaron.kenosis on Instagram"
              className="mt-6 inline-flex items-center gap-3 font-sans text-xs uppercase tracking-[0.2em] text-kenosis-ivory/80 transition-colors hover:text-kenosis-gold"
            >
              <Instagram size={16} strokeWidth={1.5} />
              @aaron.kenosis
            </a>
          </div>

          <div className="md:col-span-3">
            <h4 className="font-sans text-[0.65rem] uppercase tracking-[0.25em] text-kenosis-gold">{t("common.quickLinks")}</h4>
            <ul className="mt-6 space-y-3">
              {links.map((l) => (
                <li key={l.to}>
                  <Link
                    data-testid={`footer-link-${l.to.slice(1)}`}
                    to={l.to}
                    className="font-sans text-sm text-kenosis-ivory/70 transition-colors hover:text-kenosis-gold"
                  >
                    {l.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div className="md:col-span-4">
            <h4 className="font-sans text-[0.65rem] uppercase tracking-[0.25em] text-kenosis-gold">{t("common.newsletter")}</h4>
            <p className="mt-6 font-sans text-sm font-light text-kenosis-muted">{t("common.newsletterDesc")}</p>
            <form onSubmit={onSubscribe} className="mt-6 flex items-center border-b border-white/20 focus-within:border-kenosis-gold transition-colors">
              <input
                data-testid="newsletter-input"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={t("common.subscribePlaceholder")}
                className="flex-1 bg-transparent py-3 font-sans text-sm text-kenosis-ivory placeholder:text-kenosis-muted/60 focus:outline-none"
              />
              <button
                data-testid="newsletter-submit"
                type="submit"
                disabled={busy}
                className="font-sans text-[0.65rem] uppercase tracking-[0.2em] text-kenosis-gold disabled:opacity-50"
              >
                {t("common.subscribeCta")}
              </button>
            </form>
          </div>
        </div>

        <div className="mt-16 border-t border-white/10 pt-8">
          <p className="font-sans text-[0.7rem] uppercase tracking-[0.15em] text-kenosis-gold/70">{t("common.disclaimerTitle")}</p>
          <div className="mt-3 max-w-4xl space-y-1 font-sans text-xs leading-relaxed text-kenosis-muted/70">
            <p>{t("common.disclaimer1")}</p>
            <p>{t("common.disclaimer2")}</p>
            <p>{t("common.disclaimer3")}</p>
          </div>
          <p className="mt-8 font-sans text-xs text-kenosis-muted/60">
            © {new Date().getFullYear()} Kenosis Method. {t("common.rights")}
          </p>
        </div>
      </div>
    </footer>
  );
};
